import React, { useEffect, useRef, useCallback } from 'react';
import { HelpOverlayProps, ControlDefinition } from './types';
import { DESKTOP_CONTROLS, MOBILE_CONTROLS } from './constants';
import styles from './HelpOverlay.module.css';

export function HelpOverlay({ isOpen, onClose, deviceType }: HelpOverlayProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Store the previously focused element when overlay opens
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current = document.activeElement as HTMLElement;
      // Focus the close button when overlay opens
      closeButtonRef.current?.focus();
    } else if (previouslyFocusedRef.current) {
      // Return focus to previous element on close
      previouslyFocusedRef.current.focus();
      previouslyFocusedRef.current = null;
    }
  }, [isOpen]);

  // Focus trap: keep focus within the overlay
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key === 'Tab' && panelRef.current) {
        const focusableElements = panelRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    },
    [onClose]
  );

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (event: React.MouseEvent) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen) {
    return null;
  }

  const controls: ControlDefinition[] =
    deviceType === 'desktop' ? DESKTOP_CONTROLS : MOBILE_CONTROLS;

  return (
    <div
      className={styles.overlay}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-overlay-title"
    >
      <div className={styles.panel} ref={panelRef}>
        <button
          ref={closeButtonRef}
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close help overlay"
          type="button"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <h2 id="help-overlay-title" className={styles.title}>
          Camera Controls
        </h2>

        <ul className={styles.controlList}>
          {controls.map((control) => (
            <li key={control.key} className={styles.controlItem}>
              <span className={styles.key}>{control.key}</span>
              {control.altKey && (
                <>
                  <span className={styles.altSeparator}>/</span>
                  <span className={styles.key}>{control.altKey}</span>
                </>
              )}
              <span className={styles.description}>{control.description}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
